import { AxiosInstance } from 'axios';

import { Configuration } from '@/clients/unifiedApi';
import { UI_TIMEOUT } from '@/constants';
import notify from '@/notify';
import { useAppConfigStore } from '@/piniaStores/appConfig/appConfig.store';
import { useAuthStore } from '@/piniaStores/auth/auth.store';
import { i18n } from '@/plugins';
import { getAuth0Client } from '@/services/auth';

/**
 * Returns a configuration object for the unified API.  Throws an error if
 * the config is invalid, which is ultimately logged as a console warning.
 */
export const getApiConfig = (accessToken?: string): Configuration => {
  const appConfigStore = useAppConfigStore();
  const token = accessToken || useAuthStore().accessToken;

  return new Configuration({
    basePath: appConfigStore.unifiedApiUrl,
    baseOptions: {
      headers: {
        Authorization: `Bearer ${token}`,
        'LeafLink-Version': '2022-10-31',
      },
    },
  });
};

/**
 * Create API from OpenAPI SDK factory method
 */
export const getUnifiedApi = async <TFactory extends Record<string, unknown>>(
  apiFactory: (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) => TFactory,
): Promise<TFactory> => {
  const appConfigStore = useAppConfigStore();
  if (!appConfigStore.unifiedConfig?.enabled) {
    // At the time of writing, the unified API is not enabled in the sandbox environment.
    return Promise.resolve(apiFactory(getApiConfig()));
  }

  try {
    const accessToken = await getAuth0Client()?.getTokenSilently();
    if (!accessToken) {
      throw new Error('Auth0 client not initialized');
    }
    return apiFactory(getApiConfig(accessToken));
  } catch (error) {
    notify.warning(i18n.global.t('notify.errorNotAuthenticatedRedirect'));

    window.setTimeout(() => {
      window.location.replace('/accounts/login/?next=/');
    }, UI_TIMEOUT);

    return Promise.reject(error);
  }
};
